import { useOutletContext } from "react-router";

import { Stack } from "@mui/material";
import { ClientContent } from "@shared";

import { ClientDetailResponse, DocumentGroup } from "@/api";
import { ASSET_INVENTORY_UPLOAD_AREAS } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/assetInventoryUploadAreas";
import { AssetDocumentSection } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/components/asset-document-section/AssetDocumentSection";
import { useTranslation } from "@/translations";

import { useDocumentsOverview } from "./hooks/useDocumentsOverview";

const OVERVIEW_SECTION = "clients.client.debtSettlement.assetInventory.documentsOverview";

/**
 * Overview of every asset inventory upload area — one section per area, generated from the
 * upload-area registry. Sections support multi-select with the DropZone bulk actions (merge,
 * download, delete) plus drag & drop between sections and the DropZone panel; uploads and moves
 * are deferred and persisted on save.
 */
export function DocumentsOverviewView() {
  const { t } = useTranslation();
  const client = useOutletContext<ClientDetailResponse>();
  const { formik, onAddMove, onCancelMove } = useDocumentsOverview(client.clientId);

  const onDropFiles = (group: DocumentGroup) => (files: File[]) => {
    const current = formik.values.uploads[group] ?? [];
    void formik.setFieldValue(`uploads.${group}`, [...current, ...files]);
  };

  const onRemovePending = (group: DocumentGroup) => (index: number) => {
    const current = formik.values.uploads[group] ?? [];
    void formik.setFieldValue(
      `uploads.${group}`,
      current.filter((_, i) => i !== index)
    );
  };

  return (
    <ClientContent formik={formik}>
      {ASSET_INVENTORY_UPLOAD_AREAS.map((area) => (
        <Stack key={area.group} sx={{ bgcolor: "background.paper" }}>
          <AssetDocumentSection
            clientId={client.clientId}
            area={area}
            enableSelection
            showCount
            dense
            uploadButtonSize="small"
            uploadButtonLabel={t(`${OVERVIEW_SECTION}.uploadInto`, { title: t(area.titleKey) })}
            gap={1}
            pendingFiles={formik.values.uploads[area.group] ?? []}
            documentMoves={formik.values.documentMoves}
            onDropFiles={onDropFiles(area.group)}
            onRemovePending={onRemovePending(area.group)}
            onAddMove={onAddMove}
            onCancelMove={onCancelMove}
          />
        </Stack>
      ))}
    </ClientContent>
  );
}
